import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Typography,
  Button,
  InputNumber,
  Tag,
  message,
  Badge,
  Divider,
  Image,
  Space,
} from "antd";
import {
  ShoppingCartOutlined,
  StarFilled,
  HeartOutlined,
  ShareAltOutlined,
  StarOutlined,
  MessageOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../config/api.js";
import ProductRating from "./components/ProductRating";
import ReviewsList from "./components/ReviewsList";
import "./ProductDetailsModal.css";

const { Title, Text, Paragraph } = Typography;

const getPlainShareDescription = (value: string, productName: string) =>
  String(value || `Découvrez ${productName}`)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

interface ProductVariant {
  id: number;
  variant_label?: string;
  variant_values?: Record<string, string> | null;
  sku?: string;
  barcode_value?: string;
  price?: number;
  final_price?: number;
  image_url?: string;
  gallery_images?: string[];
  stock_quantity?: number;
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  has_stock?: boolean;
  stock_status?: string;
  available_for_purchase?: boolean;
}

interface VariantAxis {
  name: string;
  values: string[];
}

const matchesVariantOptions = (
  variant: ProductVariant,
  options: Record<string, string>
) =>
  Object.entries(options).every(
    ([name, value]) => String(variant.variant_values?.[name]) === value
  );

interface Product {
  id: number;
  name: string;
  online_description: string;
  online_price: number;
  stock_quantity: number;
  image_url: string;
  gallery_images?: string[];
  category_name: string;
  is_featured?: boolean;
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  available_for_purchase?: boolean;
  stock_status?: "en_stock" | "rupture" | "sur_commande" | "disponible";
  online_max_quantity?: number;
  reviews_count?: number;
  reviews_rating_avg?: number;
  reviews_5_star?: number;
  reviews_4_star?: number;
  reviews_3_star?: number;
  reviews_2_star?: number;
  reviews_1_star?: number;
  // Variantes (declinaisons Taille x Couleur)
  is_variant_parent?: boolean;
  variant_attributes?: VariantAxis[] | null;
  variants?: ProductVariant[];
  final_price?: number;
}

interface ProductDetailsModalProps {
  visible: boolean;
  product: Product | null;
  onClose: () => void;
  onAddToCart: (productId: number, quantity: number) => void;
  storeSlug: string;
  loading?: boolean;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  visible,
  product,
  onClose,
  onAddToCart,
  storeSlug,
  loading = false,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  // Variantes : valeurs d'axes selectionnees, ex { Taille: "M", Couleur: "Rouge" }
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const getImageUrl = (imagePath: string): string => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
    return `${process.env.REACT_APP_API_URL || ""}${cleanPath}`;
  };

  useEffect(() => {
    let sid = localStorage.getItem("store_session_id");
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem("store_session_id", sid);
    }
    setSessionId(sid);
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (visible) {
      setQuantity(1);
      setSelectedOptions({});
    }
  }, [visible, product?.id]);

  // Garder l'ordre des axes du parent, mais n'afficher que les valeurs presentes
  // dans les declinaisons publiees de cette boutique.
  const variantAxes = useMemo<VariantAxis[]>(() => {
    if (!product) return [];
    const axisMap: Record<string, Set<string>> = {};
    (product.variants || []).forEach((v) => {
      const vals = v.variant_values || {};
      Object.keys(vals).forEach((k) => {
        if (!axisMap[k]) axisMap[k] = new Set();
        axisMap[k].add(String(vals[k]));
      });
    });
    const declaredAxes = Array.isArray(product.variant_attributes)
      ? product.variant_attributes
      : [];
    const axisNames = Array.from(
      new Set([...declaredAxes.map((axis) => axis.name), ...Object.keys(axisMap)])
    );
    return axisNames
      .filter((name) => axisMap[name]?.size)
      .map((name) => {
        const declaredValues = declaredAxes.find((axis) => axis.name === name)?.values || [];
        const values = Array.from(
          new Set([
            ...declaredValues.filter((value) => axisMap[name].has(String(value))).map(String),
            ...Array.from(axisMap[name]),
          ])
        );
        return { name, values };
      });
  }, [product]);

  const selectVariantOption = (axisIndex: number, value: string) => {
    const axisName = variantAxes[axisIndex].name;
    setSelectedOptions((previous) => {
      const next = { ...previous, [axisName]: value };
      // Lorsqu'un choix anterieur change, conserver les choix suivants seulement
      // s'ils correspondent encore a une declinaison reelle.
      variantAxes.slice(axisIndex + 1).forEach((axis, offset) => {
        if (!next[axis.name]) return;
        const selectedPrefix = Object.fromEntries(
          variantAxes
            .slice(0, axisIndex + offset + 2)
            .filter((entry) => next[entry.name])
            .map((entry) => [entry.name, next[entry.name]])
        );
        if (!(product?.variants || []).some((variant) =>
          matchesVariantOptions(variant, selectedPrefix)
        )) {
          delete next[axis.name];
        }
      });
      return next;
    });
  };

  // Declinaison correspondant aux options selectionnees (null tant que tous les
  // axes ne sont pas choisis).
  const selectedVariant = useMemo<ProductVariant | null>(() => {
    if (!product || !product.is_variant_parent || !product.variants?.length) {
      return null;
    }
    const axisNames = variantAxes.map((a) => a.name);
    if (axisNames.length === 0) return null;
    if (axisNames.some((n) => !selectedOptions[n])) return null;
    return (
      product.variants.find((v) => {
        const vals = v.variant_values || {};
        return axisNames.every(
          (n) => String(vals[n]) === String(selectedOptions[n])
        );
      }) || null
    );
  }, [product, variantAxes, selectedOptions]);

  const allImages = useMemo(() => {
    if (!product) return [];

    const images: string[] = [];

    // En mode variantes, si une declinaison est selectionnee ET qu'elle possede
    // ses propres images, on affiche SA galerie + son image principale. Sinon on
    // retombe sur les images du produit parent (jamais d'ecran vide).
    const variantHasImages = Boolean(
      selectedVariant &&
        ((selectedVariant.gallery_images &&
          selectedVariant.gallery_images.length) ||
          selectedVariant.image_url)
    );
    const galleryImages = variantHasImages
      ? selectedVariant?.gallery_images
      : product.gallery_images;
    const primaryImage = variantHasImages
      ? selectedVariant?.image_url
      : product.image_url;

    // 🔧 FIX IMAGE PRINCIPALE (Juin 2026): on affiche EN PREMIER les photos de la
    //   galerie (online_images = vraies photos produit/déclinaison). L'image "principale"
    //   (`image`) est ajoutée EN REPLI (push, pas unshift) — utile quand il n'y a pas de
    //   galerie, sinon reléguée en miniature. Évite qu'une `image` legacy/de moindre
    //   qualité (ex. un .jfif) occupe le cadre principal à la place des vraies photos.
    if (galleryImages?.length) {
      images.push(...galleryImages);
    }

    if (primaryImage && !images.includes(primaryImage)) {
      images.push(primaryImage);
    }

    return images;
  }, [product, selectedVariant]);

  const shareImageSource = allImages[0] || product?.image_url || "";

  const handleAddToCart = async () => {
    if (!product) return;

    const isVariant =
      Boolean(product.is_variant_parent) &&
      Array.isArray(product.variants) &&
      product.variants.length > 0;

    if (isVariant) {
      if (!selectedVariant) {
        message.warning("Veuillez choisir les options (taille, couleur...).");
        return;
      }
      if (selectedVariant.available_for_purchase === false) {
        message.warning("Cette déclinaison est actuellement indisponible.");
        return;
      }
    }

    const targetId = isVariant && selectedVariant ? selectedVariant.id : product.id;

    setAddingToCart(true);
    try {
      await onAddToCart(targetId, quantity);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleShare = async () => {
    if (!product) return;

    const productUrl = API_CONFIG.getProductMetaUrl(
      storeSlug,
      product.id,
      shareImageSource
    );
    const description = getPlainShareDescription(
      product.online_description,
      product.name
    );

    const copyProductLink = async () => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(productUrl);
        message.success("Lien du produit copié dans le presse-papiers");
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = productUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();

      try {
        const copied = document.execCommand("copy");
        if (!copied) {
          throw new Error("La commande de copie a échoué");
        }
        message.success("Lien du produit copié dans le presse-papiers");
      } finally {
        document.body.removeChild(textArea);
      }
    };

    try {
      // Le partage natif recoit uniquement l'URL du produit. La photo reste une
      // metadonnee Open Graph utilisee pour l'apercu, jamais une piece jointe.
      if (typeof navigator.share === "function") {
        await navigator.share({
          title: product.name,
          text: description,
          url: productUrl,
        });
        message.success("Lien du produit partagé avec succès");
        return;
      }

      await copyProductLink();
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Erreur lors du partage:", error);
        message.error("Impossible de copier ou partager le lien. Veuillez réessayer.");
      }
    }
  };

  const handleFavorite = () => {
    message.info("Fonctionnalité favoris à venir");
  };

  if (!product) return null;

  const isVariantProduct =
    Boolean(product.is_variant_parent) &&
    Array.isArray(product.variants) &&
    product.variants.length > 0;
  // En mode variantes, tant qu'aucune declinaison complete n'est choisie, on
  // bloque l'achat et on affiche un prix "a partir de".
  const awaitingVariantChoice = isVariantProduct && !selectedVariant;

  // Valeurs effectives (declinaison choisie sinon parent / minimum).
  const effStockMode = ((selectedVariant?.stock_management_mode ||
    product.stock_management_mode ||
    "standard") as "standard" | "pre_order" | "unlimited");
  const stockMode = effStockMode;
  const isFeatured = Boolean(product.is_featured);
  const effStockQty = isVariantProduct
    ? selectedVariant?.stock_quantity ?? 0
    : product.stock_quantity;
  const effPrice = isVariantProduct
    ? selectedVariant
      ? selectedVariant.final_price ?? selectedVariant.price ?? 0
      : product.final_price ?? product.online_price
    : product.online_price;
  const canPurchase = awaitingVariantChoice
    ? false
    : isVariantProduct
    ? Boolean(selectedVariant && selectedVariant.available_for_purchase !== false)
    : product.available_for_purchase !== false;
  const maxQuantity =
    stockMode === "standard"
      ? Math.max(effStockQty, 1)
      : stockMode === "pre_order"
      ? product.online_max_quantity || 9999
      : 9999;
  const formattedPrice = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "XOF",
  }).format(effPrice || 0);
  const priceLabel = awaitingVariantChoice
    ? `À partir de ${formattedPrice}`
    : formattedPrice;

  let availabilityLabel = "Disponible";
  let availabilityColor: "green" | "red" | "orange" | "cyan" = "green";

  if (awaitingVariantChoice) {
    availabilityLabel = "Sélectionnez vos options";
    availabilityColor = "orange";
  } else if (stockMode === "unlimited") {
    availabilityLabel = "Toujours disponible";
    availabilityColor = "cyan";
  } else if (stockMode === "pre_order") {
    availabilityLabel = "Sur commande";
    availabilityColor = "orange";
  } else if (effStockQty > 0) {
    availabilityLabel = `En stock (${effStockQty})`;
    availabilityColor = "green";
  } else {
    availabilityLabel = "Rupture de stock";
    availabilityColor = "red";
  }

  const quantityHelper = awaitingVariantChoice
    ? "Choisissez d'abord vos options"
    : stockMode === "standard"
    ? `${effStockQty} disponible${effStockQty > 1 ? "s" : ""}`
    : stockMode === "pre_order" && product.online_max_quantity
    ? `Maximum ${product.online_max_quantity}`
    : stockMode === "unlimited"
    ? "Quantité illimitée"
    : "Choisissez votre quantité";

  const actionLabel = awaitingVariantChoice
    ? "Choisir les options"
    : !canPurchase
    ? "Produit indisponible"
    : stockMode === "pre_order"
    ? "Précommander"
    : "Ajouter au panier";

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? "100vw" : 960}
      wrapClassName={`public-store-product-modal${
        isMobile ? " public-store-product-modal--mobile" : ""
      }`}
      title={
        <div className="public-store-product-modal__title">
          {isFeatured && (
            <StarFilled className="public-store-product-modal__title-icon" />
          )}
          <span className="public-store-product-modal__title-text">
            {product.name}
          </span>
        </div>
      }
      styles={{ body: { padding: 0, overflowX: "hidden" } }}
      style={{ top: isMobile ? 0 : 24 }}
      centered={!isMobile}
      destroyOnClose
      maskClosable
    >
      <div className="public-store-product-modal__body">
        <div className="public-store-product-modal__layout">
          <div className="public-store-product-modal__media-shell">
            <div className="public-store-product-modal__media-stage">
              {allImages.length === 0 ? (
                <div className="public-store-product-modal__placeholder">
                  Aucune image disponible
                </div>
              ) : (
                <Image.PreviewGroup>
                  <div className="public-store-product-modal__main-image-wrap">
                    <Image
                      src={getImageUrl(allImages[0])}
                      alt={product.name}
                      className="public-store-product-modal__main-image"
                      preview={{
                        mask: (
                          <div className="public-store-product-modal__preview-mask">
                            <EyeOutlined />
                            <span>Voir en grand</span>
                          </div>
                        ),
                      }}
                    />

                    {isFeatured && (
                      <div className="public-store-product-modal__featured-badge">
                        <StarFilled />
                        Coup de cœur
                      </div>
                    )}
                  </div>

                  {allImages.length > 1 && (
                    <div className="public-store-product-modal__thumb-strip">
                      {allImages.slice(1).map((image, index) => (
                        <Image
                          key={`${image}-${index}`}
                          src={getImageUrl(image)}
                          alt={`${product.name} ${index + 2}`}
                          width={isMobile ? 72 : 84}
                          height={isMobile ? 72 : 84}
                          className="public-store-product-modal__thumb"
                          preview={{ mask: null }}
                        />
                      ))}
                    </div>
                  )}
                </Image.PreviewGroup>
              )}
            </div>
          </div>

          <div className="public-store-product-modal__content">
            <div className="public-store-product-modal__tag-row">
                <Tag color="blue">{product.category_name}</Tag>
                <Tag color={availabilityColor}>{availabilityLabel}</Tag>
                {isFeatured && (
                  <Tag className="public-store-product-modal__feature-tag">
                    Produit vedette
                  </Tag>
                )}
              </div>

              <div className="public-store-product-modal__price-block">
                <Title level={2} className="public-store-product-modal__price">
                  {priceLabel}
                </Title>
                <Text className="public-store-product-modal__price-note">
                  {stockMode === "pre_order"
                    ? "Précommande disponible pour ce produit."
                    : "Tarif affiché TTC selon la boutique."}
                </Text>
              </div>

              {product.online_description && (
                <div className="public-store-product-modal__section">
                  <Title
                    level={5}
                    className="public-store-product-modal__section-title"
                  >
                    Description
                  </Title>
                  <Paragraph className="public-store-product-modal__description">
                    {product.online_description}
                  </Paragraph>
                </div>
              )}

              {isVariantProduct && variantAxes.length > 0 && (
                <div className="public-store-product-modal__section">
                  {variantAxes.map((axis, axisIndex) => (
                    <div key={axis.name} style={{ marginBottom: 12 }}>
                      <Text strong style={{ display: "block", marginBottom: 6 }}>
                        {axis.name}
                      </Text>
                      <Space wrap>
                        {axis.values.map((value) => {
                          const active = selectedOptions[axis.name] === value;
                          const selectedPrefix = Object.fromEntries(
                            variantAxes
                              .slice(0, axisIndex)
                              .filter((entry) => selectedOptions[entry.name])
                              .map((entry) => [entry.name, selectedOptions[entry.name]])
                          );
                          const selectable = (product.variants || []).some((variant) =>
                            matchesVariantOptions(variant, {
                              ...selectedPrefix,
                              [axis.name]: value,
                            })
                          );
                          return (
                            <Button
                              key={value}
                              type={active ? "primary" : "default"}
                              disabled={!selectable}
                              title={!selectable ? "Combinaison indisponible" : undefined}
                              onClick={() => selectVariantOption(axisIndex, value)}
                            >
                              {value}
                            </Button>
                          );
                        })}
                      </Space>
                    </div>
                  ))}
                  {selectedVariant && (selectedVariant.sku || selectedVariant.variant_label) && (
                    <Text type="secondary">
                      Référence : {selectedVariant.sku || selectedVariant.variant_label}
                    </Text>
                  )}
                </div>
              )}

              <div className="public-store-product-modal__purchase-panel">
                <div className="public-store-product-modal__purchase-head">
                  <div>
                    <Text strong>Quantité</Text>
                    <Text className="public-store-product-modal__quantity-help">
                      {quantityHelper}
                    </Text>
                  </div>
                  <InputNumber
                    min={1}
                    max={maxQuantity}
                    value={quantity}
                    onChange={(value) => setQuantity(value || 1)}
                    disabled={!canPurchase}
                    size={isMobile ? "large" : "middle"}
                    className="public-store-product-modal__quantity-input"
                  />
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  loading={addingToCart || loading}
                  disabled={!canPurchase}
                  onClick={handleAddToCart}
                  className={`public-store-product-modal__cart-button${
                    stockMode === "pre_order"
                      ? " public-store-product-modal__cart-button--preorder"
                      : ""
                  }`}
                  block
                >
                  {actionLabel}
                </Button>

                <div className="public-store-product-modal__secondary-actions">
                  <Button
                    type="text"
                    icon={<HeartOutlined />}
                    onClick={handleFavorite}
                  >
                    Favoris
                  </Button>
                  <Button
                    type="text"
                    icon={<ShareAltOutlined />}
                    onClick={handleShare}
                  >
                    Partager
                  </Button>
                </div>
              </div>
            </div>

            <div className="public-store-product-modal__benefits">
              <div className="public-store-product-modal__benefit-card">
                <div className="public-store-product-modal__benefit-icon">
                  🚚
                </div>
                <Text strong>Livraison</Text>
                <Text type="secondary">Selon votre zone et le stock.</Text>
              </div>
              <div className="public-store-product-modal__benefit-card">
                <div className="public-store-product-modal__benefit-icon">
                  🔒
                </div>
                <Text strong>Paiement sécurisé</Text>
                <Text type="secondary">Plusieurs moyens disponibles.</Text>
              </div>
              <div className="public-store-product-modal__benefit-card">
                <div className="public-store-product-modal__benefit-icon">
                  📞
                </div>
                <Text strong>Support client</Text>
                <Text type="secondary">Une assistance en cas de besoin.</Text>
              </div>
            </div>
        </div>

        <div className="public-store-product-modal__reviews-section">
          <Divider className="public-store-product-modal__divider" />

          <div className="public-store-product-modal__reviews-head">
            <Title
              level={3}
              className="public-store-product-modal__reviews-title"
            >
              <MessageOutlined />
              <span>Avis clients</span>
              {product.reviews_count !== undefined && product.reviews_count > 0 && (
                <Badge
                  count={product.reviews_count}
                  style={{ backgroundColor: "#52c41a" }}
                />
              )}
            </Title>
            <Text className="public-store-product-modal__reviews-subtitle">
              Consultez les retours clients avant de commander.
            </Text>
          </div>

          {product.reviews_count !== undefined && product.reviews_count > 0 ? (
            <div className="public-store-product-modal__reviews-content">
              <ProductRating
                rating={product.reviews_rating_avg || 0}
                reviewsCount={product.reviews_count}
                distribution={{
                  5: product.reviews_5_star || 0,
                  4: product.reviews_4_star || 0,
                  3: product.reviews_3_star || 0,
                  2: product.reviews_2_star || 0,
                  1: product.reviews_1_star || 0,
                }}
                showDistribution
                size={isMobile ? "small" : "medium"}
              />

              <Divider className="public-store-product-modal__divider" />

              <div className="public-store-product-modal__reviews-list">
                <ReviewsList
                  productId={product.id}
                  storeSlug={storeSlug}
                  sessionId={sessionId}
                  pageSize={isMobile ? 4 : 5}
                  showFilters={!isMobile}
                />
              </div>
            </div>
          ) : (
            <div className="public-store-product-modal__empty-state">
              <StarOutlined className="public-store-product-modal__empty-icon" />
              <Title level={4}>Aucun avis pour le moment</Title>
              <Text type="secondary">
                Soyez le premier à partager votre expérience sur ce produit.
              </Text>
            </div>
          )}

          <div className="public-store-product-modal__review-note">
            <MessageOutlined />
            <span>
              Les avis sont proposés après commande afin de garder des retours
              utiles et vérifiés.
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductDetailsModal;
